<template>
  <AIVDialog
    v-model="isVisible"
    :title="modalTitle"
    width="90%"
    height="85vh"
    class="email-authorization-modal"
  >
    <div class="email-preview-container" v-if="notification">
      <!-- Client Info at the top -->
      <div class="client-info-section">
        <h5 class="section-header">
          <i class="fa fa-user mr-2"></i>
          Client Information
        </h5>
        <div class="client-info-grid">
          <div class="info-item">
            <label>Client Name:</label>
            <span>{{ notification.metadata?.clientName }}</span>
          </div>
          <div class="info-item">
            <label>Authorization #:</label>
            <span>{{ notification.metadata?.authNumber }}</span>
          </div>
          <div class="info-item">
            <label>Total Hours:</label>
            <span>{{ notification.metadata?.totalHours || "N/A" }}</span>
          </div>
          <div class="info-item">
            <label>Hours Used:</label>
            <span>{{ notification.metadata?.hoursUsed || "N/A" }}</span>
          </div>
          <div class="info-item">
            <label>Hours Remaining:</label>
            <span class="text-danger font-weight-bold">
              {{ notification.metadata?.hoursRemaining }} hours
            </span>
          </div>
          <div class="info-item" v-if="notification.metadata?.expirationDate">
            <label>Expiration:</label>
            <span>{{ formatDate(notification.metadata.expirationDate) }}</span>
          </div>
        </div>
      </div>

      <!-- Email Fields -->
      <div class="email-fields-section">
        <h5 class="section-header">
          <i class="fa fa-envelope mr-2"></i>
          Email Details
        </h5>
        <div class="email-fields">
          <div class="field-group">
            <label>To:</label>
            <input
              type="email"
              v-model="emailTo"
              class="form-control"
              placeholder="recipient@example.com"
            />
          </div>
          <div class="field-group">
            <label>CC (Office Admin):</label>
            <input
              type="email"
              v-model="adminEmail"
              class="form-control"
              placeholder="Office admin will be CC'd automatically"
              readonly
            />
          </div>
          <div class="field-group">
            <label>Subject:</label>
            <input
              type="text"
              v-model="emailSubject"
              class="form-control"
              placeholder="Authorization Request"
            />
          </div>
          <div class="field-group">
            <label>Hours Requested:</label>
            <input
              type="number"
              v-model.number="hoursRequested"
              class="form-control"
              placeholder="Hours"
              step="0.5"
              min="0"
            />
          </div>
        </div>
      </div>

      <!-- Email Content -->
      <div class="email-content-section">
        <h5 class="section-header">
          <i class="fa fa-file-text mr-2"></i>
          Email Content
          <small
            class="ml-2 text-muted"
            style="font-size: 0.85rem; font-weight: normal"
          >
            (Editable - modify as needed)
          </small>
        </h5>

        <!-- Editable Email Content Textarea -->
        <div class="email-content-editor">
          <textarea
            v-model="emailContent"
            class="form-control email-textarea"
            rows="15"
            placeholder="Enter email content here..."
          ></textarea>
        </div>
      </div>
    </div>

    <div v-if="showConfirmReject" class="confirmation-overlay">
      <AIVDialog
        v-model="showConfirmReject"
        title="Confirm Rejection"
        width="500px"
        class="confirmation-dialog"
      >
        <div class="confirmation-content">
          <div class="text-center mb-3">
            <i class="fa fa-exclamation-triangle fa-3x text-warning"></i>
          </div>
          <h5 class="text-center">Are you sure?</h5>
          <p class="text-center text-muted">
            This will prevent the email from being sent to the counselor. This
            action cannot be undone.
          </p>

          <div class="form-group mt-3">
            <label for="rejectReason">Reason for rejection (required):</label>
            <textarea
              id="rejectReason"
              v-model="rejectReason"
              class="form-control"
              rows="3"
              placeholder="Please provide a reason for rejecting this notification..."
              required
            ></textarea>
          </div>
        </div>

        <template slot="footer">
          <button
            @click="showConfirmReject = false"
            class="btn btn-secondary mr-2"
          >
            Cancel
          </button>
          <button
            @click="confirmReject"
            class="btn btn-danger"
            :disabled="!rejectReason.trim() || processing"
          >
            <span v-if="processing">
              <i class="fa fa-spinner fa-spin mr-1"></i> Processing...
            </span>
            <span v-else>
              <i class="fa fa-times mr-1"></i> Yes, Reject Email
            </span>
          </button>
        </template>
      </AIVDialog>
    </div>

    <!-- Prompt Editor Modal -->
    <AIVDialog
      v-model="showPromptEditor"
      title="Edit AI Prompt"
      width="90%"
      height="85vh"
      class="prompt-editor-modal"
    >
      <div class="prompt-editor-content">
        <p class="text-muted mb-3">
          <i class="fa fa-info-circle"></i>
          Edit the instructions below to customize how the AI generates the
          email. Tokens <code v-pre>{{clientName}}</code>,
          <code v-pre>{{counselorName}}</code> and
          <code v-pre>{{lifeSkillsCourses}}</code> are filled in automatically;
          client notes, formatting rules, and shift notes are appended by the
          system.
        </p>

        <div class="form-group">
          <label class="font-weight-bold">AI Prompt Instructions:</label>
          <textarea
            v-model="customPrompt"
            class="form-control prompt-textarea"
            placeholder="Enter custom prompt instructions..."
          ></textarea>
        </div>
      </div>

      <template slot="footer">
        <button
          @click="showPromptEditor = false"
          class="btn btn-secondary mr-2"
        >
          Cancel
        </button>
        <button
          @click="resetPromptToDefault"
          class="btn btn-outline-primary mr-2"
        >
          <i class="fa fa-undo"></i> Reset to Saved
        </button>
        <button
          v-if="isAdmin && servicePromptRow"
          @click="$confirmAction(saveAsDefaultPrompt)"
          class="btn btn-outline-primary mr-2"
          :disabled="savingPromptDefault"
          :title="`Save this prompt as the default for every future ${servicePromptRow.code} authorization request`"
        >
          <span v-if="savingPromptDefault">
            <i class="fa fa-spinner fa-spin mr-1"></i> Saving...
          </span>
          <span v-else>
            <i class="fa fa-save mr-1"></i>
            Save as Default for {{ servicePromptRow.code }}
          </span>
        </button>
        <button
          @click="generateWithCustomPrompt"
          class="btn btn-primary"
          :disabled="regenerating"
        >
          <span v-if="regenerating">
            <i class="fa fa-spinner fa-spin mr-1"></i> Generating...
          </span>
          <span v-else> <i class="fa fa-magic mr-1"></i> Generate Email </span>
        </button>
      </template>
    </AIVDialog>

    <template slot="footer">
      <div class="modal-footer-content">
        <button @click="closeModal" class="btn btn-secondary mr-2">
          Cancel
        </button>

        <button
          @click="regenerateAIContent"
          class="btn btn-primary mr-2"
          :disabled="regenerating || processing"
        >
          <span v-if="regenerating">
            <i class="fa fa-spinner fa-spin mr-1"></i> Regenerating...
          </span>
          <span v-else>
            <i class="fa fa-refresh mr-1"></i> Regenerate Email
          </span>
        </button>

        <button
          @click="handleApprove"
          class="btn btn-success mr-2"
          :disabled="processing || regenerating"
        >
          <span v-if="processing">
            <i class="fa fa-spinner fa-spin mr-1"></i> Processing...
          </span>
          <span v-else>
            <i class="fa fa-check mr-1"></i> Approve & Send Email
          </span>
        </button>

        <button
          @click="handleReject"
          class="btn btn-danger"
          :disabled="processing"
        >
          <i class="fa fa-times mr-1"></i> Decline
        </button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import { notificationsService } from "@/serviceHandlers/notificationsService.js";
import { serviceCodesService } from "@/serviceHandlers/serviceCodesService";
import { authorizationEmailsService } from "@/serviceHandlers/authorizationEmailsService";
import { useLoginStore } from "@/stores/loginStore";

export default {
  name: "EmailAuthorizationModal",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    notification: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      processing: false,
      regenerating: false,
      showConfirmReject: false,
      showPromptEditor: false,
      customPrompt: "",
      // Per-service-code prompts fetched from the API (service_codes.ai_email_prompt)
      servicePromptRow: null,
      genericDefaultPrompt: "",
      savingPromptDefault: false,
      rejectReason: "",
      emailSubject: "",
      emailTo: "",
      adminEmail: "",
      emailContent: "",
      hoursRequested: null,
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    modalTitle() {
      return "Email Authorization Request";
    },
    isAdmin() {
      return useLoginStore()?.user?.user_type === "Admin";
    },
  },
  watch: {
    isVisible: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.notification) {
          // Initialize email fields when modal opens
          const serviceCode = this.notification?.metadata?.serviceCode
            ? ` (${this.notification.metadata.serviceCode})`
            : "";
          this.emailSubject = `Authorization Request${serviceCode}`;
          this.emailTo = this.getRecipientEmail();
          this.adminEmail = this.getAdminEmail();
          this.emailContent = this.getEmailContent();
          this.hoursRequested = this.notification?.metadata?.totalHours || null;
        } else if (!newVal) {
          this.showConfirmReject = false;
          this.rejectReason = "";
          this.processing = false;
        }
      },
    },
    notification: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.isVisible) {
          // Update subject when notification changes
          const serviceCode = newVal?.metadata?.serviceCode
            ? ` (${newVal.metadata.serviceCode})`
            : "";
          this.emailSubject = `Authorization Request${serviceCode}`;
          this.emailTo = this.getRecipientEmail();
          this.adminEmail = this.getAdminEmail();
          this.emailContent = this.getEmailContent();
          this.hoursRequested = newVal?.metadata?.totalHours || null;
        }
      },
    },
  },
  methods: {
    getRecipientEmail() {
      if (this.notification?.combinedEmails) {
        return this.notification.combinedEmails;
      }
      return "";
    },

    getAdminEmail() {
      // Prefer the dynamically-fetched adminEmail (top-level field from dashboard API)
      if (this.notification?.adminEmail) {
        return this.notification.adminEmail;
      }
      // Fallback to metadata adminEmail (set at notification creation time)
      if (this.notification?.metadata?.adminEmail) {
        return this.notification.metadata.adminEmail;
      }
      // Fallback to email_cc (set after email is sent)
      if (this.notification?.email_cc) {
        return this.notification.email_cc;
      }
      return "";
    },

    getEmailContent() {
      if (!this.notification?.metadata) {
        return "";
      }

      // Check for AI error first - display it as the email content
      if (this.notification.aiError || this.notification.metadata.aiError) {
        return this.notification.aiError || this.notification.metadata.aiError;
      }

      // Check for AI-generated content
      if (
        this.notification.metadata.aiCleanContent &&
        Array.isArray(this.notification.metadata.aiCleanContent)
      ) {
        return this.notification.metadata.aiCleanContent.join("\n\n");
      }

      if (this.notification.metadata.aiContent) {
        return this.notification.metadata.aiContent;
      }

      // Fallback to a basic message if no content is available
      return `Authorization ${this.notification.metadata.authNumber} for ${this.notification.metadata.clientName} has ${this.notification.metadata.hoursRemaining} hours remaining.`;
    },

    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    async handleApprove() {
      if (this.processing) return;

      this.processing = true;
      try {
        const response = await notificationsService.approveAuthorizationAlert(
          this.notification.id,
          {
            notes: "Approved via email preview modal",
            emailSubject: this.emailSubject,
            emailTo: this.emailTo,
            emailContent: this.emailContent,
            hoursRequested: this.hoursRequested,
          },
        );

        if (response && response.success) {
          this.$notifySuccess(
            "The authorization alert email has been sent successfully.",
          );

          this.$emit("approved", response.notification || this.notification);
          this.closeModal();
        } else {
          this.$notifyError(
            response?.message ||
              "The alert was approved but the email could not be sent.",
          );
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.error ||
          error.message ||
          "Failed to approve the authorization alert.";
        this.$notifyError(errorMessage);
      } finally {
        this.processing = false;
      }
    },

    handleReject() {
      if (this.processing) return;
      this.showConfirmReject = true;
    },

    async confirmReject() {
      if (this.processing || !this.rejectReason.trim()) return;

      this.processing = true;
      try {
        const response = await notificationsService.rejectAuthorizationAlert(
          this.notification.id,
          {
            notes: this.rejectReason.trim(),
          },
        );

        if (response.success) {
          this.$notifyInfo(
            "The authorization alert has been declined and will not be sent.",
          );

          this.$emit("rejected", this.notification);
          this.showConfirmReject = false;
          this.closeModal();
        } else {
          this.$notifyError(
            response.error || "Failed to reject the authorization alert.",
          );
        }
      } catch (error) {
        this.$notifyError(
          error.response?.data?.error ||
            "Failed to reject the authorization alert.",
        );
      } finally {
        this.processing = false;
      }
    },

    closeModal() {
      this.isVisible = false;
    },

    async regenerateAIContent() {
      // Open the prompt editor seeded with the service code's saved prompt
      // (service_codes.ai_email_prompt) or the generic default. Prompts are
      // shown raw — {{clientName}}/{{counselorName}}/{{lifeSkillsCourses}}
      // tokens are substituted by the backend at generation time.
      try {
        const res = await authorizationEmailsService.getSupportedCodes();
        const codes = res.supportedCodes || [];
        const serviceCode = this.notification?.metadata?.serviceCode;
        this.servicePromptRow =
          codes.find((sc) => sc.code === serviceCode) || null;
        this.genericDefaultPrompt = res.genericDefaultPrompt || "";
      } catch (error) {
        this.servicePromptRow = null;
        this.$notifyError("Could not load the saved AI prompt: " + error);
      }
      this.customPrompt =
        this.servicePromptRow?.ai_email_prompt || this.genericDefaultPrompt;
      this.showPromptEditor = true;
    },

    resetPromptToDefault() {
      this.customPrompt =
        this.servicePromptRow?.ai_email_prompt || this.genericDefaultPrompt;
    },

    async saveAsDefaultPrompt() {
      if (!this.servicePromptRow || this.savingPromptDefault) return;
      const value = (this.customPrompt || "").trim() || null;
      this.savingPromptDefault = true;
      try {
        await serviceCodesService.update(this.servicePromptRow.id, {
          ai_email_prompt: value,
        });
        this.servicePromptRow.ai_email_prompt = value;
        this.$notifySuccess(
          `Saved as the default AI prompt for ${this.servicePromptRow.code}. ` +
            `You can also manage it in Settings → AI Email Prompts.`,
        );
      } catch (error) {
        this.$notifyError("Could not save the prompt: " + error);
      } finally {
        this.savingPromptDefault = false;
      }
    },

    async generateWithCustomPrompt() {
      if (this.regenerating || !this.notification?.id) return;

      this.regenerating = true;
      try {
        // Send the instructions as-is; the backend appends data context and
        // the critical formatting rules itself.
        const response = await notificationsService.regenerateAIContent(
          this.notification.id,
          { customPrompt: this.customPrompt },
        );

        // Handle both response.data and direct response formats
        const data = response.data || response;

        if (data.success) {
          // Update the email content with the new AI-generated content
          if (data.notification?.metadata) {
            const metadata = data.notification.metadata;
            if (
              metadata.aiCleanContent &&
              Array.isArray(metadata.aiCleanContent)
            ) {
              this.emailContent = metadata.aiCleanContent.join("\n\n");
            } else if (metadata.aiContent) {
              this.emailContent = metadata.aiContent;
            }
          }

          // Close the prompt editor
          this.showPromptEditor = false;

          // Emit event to parent to refresh the notification data with the updated notification
          this.$emit("regenerated", data.notification);

          // Show success notification AFTER all updates are complete
          this.$notifySuccess("AI content regenerated successfully!");
        } else {
          this.$notifyError(data.error || "Failed to regenerate AI content");
        }
      } catch (error) {
        this.$notifyError(error.message || "Failed to regenerate AI content");
      } finally {
        this.regenerating = false;
      }
    },
  },
};
</script>

<style scoped>
.email-preview-container {
  padding: 1.5rem;
  background-color: #2c2c2c;
  color: #e0e0e0;
}

.section-header {
  color: #ffffff;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #4a4a4a;
}

/* Client Info Section */
.client-info-section {
  background: #3f3f3f;
  padding: 1.25rem;
  border-radius: 0.5rem;
  border: 1px solid #4a4a4a;
  margin-bottom: 1.5rem;
}

.client-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-item label {
  color: #b0b0b0;
  font-size: 0.85rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-item span {
  color: #ffffff;
  font-size: 1rem;
  font-weight: 400;
}

/* Email Fields Section */
.email-fields-section {
  background: #3f3f3f;
  padding: 1.25rem;
  border-radius: 0.5rem;
  border: 1px solid #4a4a4a;
  margin-bottom: 1.5rem;
}

.email-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.field-group label {
  color: #ffffff;
  font-weight: 500;
}

.form-control {
  background-color: #2c2c2c;
  border: 1px solid #4a4a4a;
  color: #e0e0e0;
  padding: 0.5rem;
  border-radius: 0.25rem;
}

.form-control:focus {
  background-color: #2c2c2c;
  border-color: #74b9ff;
  color: #e0e0e0;
  box-shadow: 0 0 0 0.2rem rgba(116, 185, 255, 0.25);
  outline: none;
}

.form-control::placeholder {
  color: #b0b0b0;
}

/* Email Content Section */
.email-content-section {
  background: #3f3f3f;
  padding: 1.25rem;
  border-radius: 0.5rem;
  border: 1px solid #4a4a4a;
}

.email-content-section .section-header {
  color: #ffffff;
  border-bottom-color: #4a4a4a;
}

.email-content-box {
  background: #2c2c2c;
  padding: 1rem;
  border-radius: 0.25rem;
  border: 1px solid #4a4a4a;
}

.email-text-content {
  background: #363636;
  padding: 1rem;
  border-radius: 0.25rem;
  border: 1px solid #4a4a4a;
  max-height: 400px;
  overflow-y: auto;
  margin-top: 1rem;
}

.email-paragraph {
  margin-bottom: 1rem;
  line-height: 1.6;
  color: #e0e0e0;
  text-align: justify;
}

.email-paragraph:last-child {
  margin-bottom: 0;
}

.email-content-editor {
  margin-top: 0.5rem;
}

.email-textarea {
  background-color: #2c2c2c;
  border: 1px solid #4a4a4a;
  color: #e0e0e0;
  padding: 0.75rem;
  border-radius: 0.25rem;
  font-family: inherit;
  line-height: 1.6;
  resize: vertical;
  min-height: 300px;
  width: 100%;
}

.email-textarea:focus {
  background-color: #2c2c2c;
  border-color: #74b9ff;
  color: #e0e0e0;
  box-shadow: 0 0 0 0.2rem rgba(116, 185, 255, 0.25);
  outline: none;
}

.email-textarea::placeholder {
  color: #b0b0b0;
}

.text-danger {
  color: #fd79a8 !important;
}

.text-muted {
  color: #b0b0b0 !important;
}

.font-weight-bold {
  font-weight: 600 !important;
}

.modal-footer-content {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.confirmation-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.confirmation-dialog {
  background: #3f3f3f;
}

.confirmation-content {
  padding: 1rem;
  color: #e0e0e0;
}

.confirmation-content h5 {
  color: #ffffff;
}

/* Scrollbar styles for dark theme */
.email-text-content::-webkit-scrollbar {
  width: 8px;
}

.email-text-content::-webkit-scrollbar-track {
  background: #2c2c2c;
  border-radius: 4px;
}

.email-text-content::-webkit-scrollbar-thumb {
  background: #4a4a4a;
  border-radius: 4px;
}

.email-text-content::-webkit-scrollbar-thumb:hover {
  background: #6a6a6a;
}

.prompt-editor-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 500px;
}

.prompt-editor-content .text-muted {
  flex-shrink: 0;
}

.prompt-editor-content .form-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-bottom: 0;
}

.prompt-editor-content .form-group label {
  color: white;
  margin-bottom: 0.5rem;
  flex-shrink: 0;
}

.prompt-editor-content .prompt-textarea {
  font-family: "Courier New", Courier, monospace;
  font-size: 13px;
  line-height: 1.6;
  flex: 1;
  min-height: 0;
  resize: none;
  overflow-y: auto !important;
}

@media (max-width: 768px) {
  .email-preview-container {
    padding: 1rem;
  }

  .client-info-grid {
    grid-template-columns: 1fr;
  }

  .email-fields {
    grid-template-columns: 1fr;
  }
}
</style>

<style>
/* Non-scoped styles for prompt editor modal to override el-dialog */
.prompt-editor-modal .el-dialog__body {
  display: flex !important;
  flex-direction: column !important;
}

.prompt-editor-modal .dialog-scroll-wrapper {
  height: 100% !important;
  max-height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
}
</style>
